import { createMiddleware, createStart } from '@tanstack/react-start'

/**
 * The router strips a trailing slash on its own, but answers with a temporary
 * 307 — search engines then keep the slashed URL in their database instead of
 * transferring its signals to the clean one. Claiming it permanently here, in
 * front of the router, is the only place the status code is ours to pick.
 */
const trailingSlashMiddleware = createMiddleware({ type: 'request' }).server(
  ({ request, pathname, next, handlerType }) => {
    if (handlerType === 'router' && pathname.length > 1 && pathname.endsWith('/')) {
      const url = new URL(request.url)
      url.pathname = pathname.replace(/\/+$/, '')

      return new Response(null, {
        status: 301,
        headers: { Location: url.pathname + url.search },
      })
    }

    return next()
  },
)

// Crawlers and live fetchers of the AI assistants. GA never sees them (no JS),
// so a log line per request is the only way to tell whether the agent-facing
// pages (llms.txt, *.md) get read — search the Amplify compute logs for `ai_bot`.
const AI_BOT_PATTERN =
  /GPTBot|OAI-SearchBot|ChatGPT-User|ClaudeBot|Claude-User|Claude-SearchBot|anthropic-ai|PerplexityBot|Perplexity-User|Google-CloudVertexBot|CCBot|meta-externalagent|Amazonbot|Applebot|Bytespider|DuckAssistBot|MistralAI-User|cohere-ai/

const aiBotLogMiddleware = createMiddleware({ type: 'request' }).server(async ({ request, pathname, next }) => {
  const bot = request.headers.get('user-agent')?.match(AI_BOT_PATTERN)?.[0]
  const result = await next()
  if (bot) {
    // eslint-disable-next-line no-console -- one structured JSON line per AI-bot request into the Amplify compute logs (see comment above)
    console.info(
      JSON.stringify({
        event: 'ai_bot',
        bot,
        path: pathname,
        status: result.response.status,
      }),
    )
  }
  return result
})

export const startInstance = createStart(() => ({
  requestMiddleware: [aiBotLogMiddleware, trailingSlashMiddleware],
}))
